// Page: entry-found  (non-Illinois "project found" sign-up)
//
// Ported from the inline <script> in
//   Enrollment_modules/entry-found/offer-available.module/module.html (lines 99–1202).
//
// This is the NON-Illinois sibling of the entry-il-found pilot; its logic is a
// simpler subset (email + password only — no name/date/phone/LMI/Bayou/progress
// bar). MECHANICAL / FAITHFUL PORT — runtime behaviour is preserved. The only
// deliberate changes, consistent with the pilot
// (docs/handoff/global-js-deep-refactor-notes.md):
//   1. Selectors that were scoped to the (HubSpot render-time) wrapper id
//      `#Page_0_Project_Found_widget` are now scoped to `root` (the element
//      carrying data-ea-module). Page-global selectors (body, button, `a`, the
//      overlay ids read via getElementById) stay document-scoped, as in the
//      original.
//   2. `_hcms/api/*` fetches and same-origin redirects are ROOT-RELATIVE
//      (via core/api `apiUrl`) instead of hardcoding https://enrollment.nautilussolar.com.
//   3. The one HubDB read is built with core/hubdb `hubdbRowsUrl` (table id +
//      portal from window.__ENV__) instead of an inline literal.
//   4. `validatePass` is imported from ui/validation (was a leaked global).
//   6. `openPopuppf` (a markup `onclick=` target) was a top-level global in the
//      inline script; ported into init it is no longer window-global, so the
//      popup wiring no longer resolves. It is kept and `void`-ed. The Google
//      Maps `initMap` callback that lived alongside it has been REMOVED entirely
//      (retired dead code — the map never rendered post-migration), matching the
//      pilot's dropped `initMapIl`.
//
// Dynamic ES5 patterns (vars reassigned across types, API/HubDB row shapes) are
// typed `any` to keep the port mechanical.
//
// External globals (crypt, ire, clearDatabase) are declared ambiently in the
// theme-root global.d.ts.  dealOwner and first_visited_url were implicit window
// globals in the original but are now recovered as locals inside init().

import { apiUrl } from "../../core/api"
import { hubdbRowsUrl } from "../../core/hubdb"
import * as storage from "../../core/storage"
import { validatePass } from "../../ui/validation"

// config is unused: the original inline script read everything from localStorage
// and window.__ENV__ (zero HubL tokens), so there are no HubL values to pass in.
export function init(root: HTMLElement, _config: unknown): void {
__ea_trace("modules/entry/entry-found.ts","init",[root,_config],["root","_config"]);
  // ─── Scoped DOM helpers ──────────────────────────────────────────────────────
  const q = (sel: string) => root.querySelector(sel) as HTMLElement
  const qi = (sel: string) => root.querySelector(sel) as HTMLInputElement
  const qa = (sel: string) => root.querySelectorAll(sel)
  const di = (id: string) => document.getElementById(id) as HTMLElement
  const fieldVal = (sel: string): string => String($(sel).val() ?? "")

  // `first_visited_url` and `dealOwner` were implicit window globals in the
  // original sloppy-mode inline scripts, set by entry-form's <script> which ran
  // on the same page.  In the module architecture each init() has its own
  // closure; recover the values from localStorage (dealOwner, written by
  // entry-form when it resolves broker attribution) and current href.
  const first_visited_url: string = window.location.href
  const dealOwner: any = storage.rawGet("dealOwner") ?? 0

  // ─── Shared state (was module-scoped in the inline script) ────────────────────
  let responseFromAPI: any = ""

  // ─── Loader ─────────────────────────────────────────────────────────────────
  function openLoader(): void {
__ea_trace("modules/entry/entry-found.ts","openLoader",[],[]);
    const loader = di("pageFoundLoader")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PFloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }

  function closeLoader(): void {
__ea_trace("modules/entry/entry-found.ts","closeLoader",[],[]);
    const loader = di("pageFoundLoader")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PFloaderOverlay").style.display = "none"
    loader.style.display = "none"
    buttons.forEach((button) => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[button],["button"]);
      button.disabled = false
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = false
    })
  }

  // ─── Field validation ─────────────────────────────────────────────────────────
  function validateEmail(): void {
__ea_trace("modules/entry/entry-found.ts","validateEmail",[],[]);
    q("#allfield_error_message").innerHTML = ""
    const userExistMessage = q("#userAlreadyExist")
    userExistMessage.style.display = "none"
    const email = qi("#email").value.trim()
    if (email === "") {
      q("#email_error_message").innerHTML = "Please enter a valid email address"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      q("#email_error_message").innerHTML = "Please enter a valid email address."
    } else {
      q("#email_error_message").innerHTML = ""
    }
  }

  function validatePassword(): void {
__ea_trace("modules/entry/entry-found.ts","validatePassword",[],[]);
    q("#allfield_error_message").innerHTML = ""
    const userExistMessage = q("#userAlreadyExist")
    userExistMessage.style.display = "none"
    const password = qi("#password").value
    const validPassword = validatePass(password)
    if (validPassword.valid) {
      q("#password_error_message").innerHTML = ""
    } else {
      q("#password_error_message").innerHTML = validPassword.errors[0]
    }
  }

  function validateForm(): boolean {
__ea_trace("modules/entry/entry-found.ts","validateForm",[],[]);
    const userExistMessage = q("#userAlreadyExist")
    userExistMessage.style.display = "none"
    validateEmail()
    validatePassword()

    const errorMessages = qa(".error-message")
    let hasErrors = false
    for (let i = 0; i < errorMessages.length; i++) {
      if (errorMessages[i].innerHTML !== "") {
        hasErrors = true
        break
      }
    }

    if (hasErrors) {
      q("#allfield_error_message").innerHTML = "Please complete the form and correct any errors to proceed."
      return false
    } else {
      q("#allfield_error_message").innerHTML = ""
      getContactByEmail()
    }
    return true
  }

  async function delay(ms: number): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","delay",[ms],["ms"]);
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  void delay

  // ─── Contact / deal flow ──────────────────────────────────────────────────────
  async function getContactByEmail(): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","getContactByEmail",[],[]);
    const userExistMessage = q("#userAlreadyExist")
    userExistMessage.style.display = "none"
    let email = fieldVal("#project_found-form #email")
    email = email.toLowerCase()
    try {
      openLoader()
      const data = { email: email }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      const dataa = await response.json()

      if (dataa.message === "No User Found") {
        await AddEmailPassInContact()
      } else if (dataa.message === "User Found") {
        const contactId2 = dataa.userValue.id
        const getPassword = dataa.userValue.properties.password
        await GetAssosiatedDealRecord(contactId2, getPassword)
      }
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function updateDataToContactRecord(): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","updateDataToContactRecord",[],[]);
    let email = fieldVal("#project_found-form #email")
    email = email.toLowerCase()
    let password = fieldVal("#project_found-form #password")
    password = crypt.encrypt(password)
    const u_id = storage.get("utility_id")
    const signUserDevice = storage.get("User Device")
    const signBrowserName = storage.get("Browser Name")
    const signOprSys = storage.get("Operating System")
    let upd_attcode: any = storage.get("attcode")
    let upd_repid: any = storage.get("repid")
    let upd_referralUrl: any = storage.get("referralUrl")
    if (upd_attcode == null) {
      upd_attcode = ""
      upd_repid = ""
      upd_referralUrl = first_visited_url
    } else {
      upd_referralUrl = first_visited_url
    }
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          password: password,
          first_visited_url: first_visited_url,
          internal_contact_source: "enrollment website",
          enrollment_utility_id: u_id,
          user_device: signUserDevice,
          user_agent: signBrowserName,
          user_agent___operating_system: signOprSys,
          attribution_code: upd_attcode,
          attribution_rep: upd_repid,
          referral_url: upd_referralUrl,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        await getContactIdByEmail()
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function GetAssosiatedDealRecord(contactId2: any, getPassword: any): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","GetAssosiatedDealRecord",[contactId2,getPassword],["contactId2","getPassword"]);
    void getPassword
    let _catchdata: any = ""
    try {
      const data = { contactId: contactId2 }
      const response = await fetch(apiUrl("FetchAssociatedDealsBasedOnContactId"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      _catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const dealRecordLength = dataa.userValue.associations.deals.results.length
      if (dealRecordLength >= 1) {
        closeLoader()
        const userExistMessage = q("#userAlreadyExist")
        userExistMessage.innerHTML =
          "<p>A user with this email address already exists. Please click below to log in.</p>"
        userExistMessage.style.display = "block"
      } else {
        await updateDataToContactRecord()
      }
      if (response.status === 500 || response.status === 404) {
        await updateDataToContactRecord()
      }
    } catch (error) {
      await updateDataToContactRecord()
      console.error("Error:", error)
    }
  }

  async function AddEmailPassInContact(): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","AddEmailPassInContact",[],[]);
    let email = fieldVal("#project_found-form #email")
    email = email.toLowerCase()
    let password = fieldVal("#project_found-form #password")
    password = crypt.encrypt(password)
    const u_id = storage.get("utility_id")
    const signUserDevice = storage.get("User Device")
    const signBrowserName = storage.get("Browser Name")
    const signOprSys = storage.get("Operating System")
    let upd_attcode: any = storage.get("attcode")
    let upd_repid: any = storage.get("repid")
    let upd_referralUrl: any = storage.get("referralUrl")
    if (upd_attcode == null) {
      upd_attcode = ""
      upd_repid = ""
      upd_referralUrl = first_visited_url
    } else {
      upd_referralUrl = first_visited_url
    }
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          password: password,
          first_visited_url: first_visited_url,
          internal_contact_source: "enrollment website",
          enrollment_utility_id: u_id,
          user_device: signUserDevice,
          user_agent: signBrowserName,
          user_agent___operating_system: signOprSys,
          attribution_code: upd_attcode,
          attribution_rep: upd_repid,
          referral_url: upd_referralUrl,
        },
      }
      const response = await fetch(apiUrl("AddContactRecordInHubspot"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.status === 200) {
        await getContactIdByEmail()
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function UpdateContactIdFromRecordId(id: any): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","UpdateContactIdFromRecordId",[id],["id"]);
    let email = fieldVal("#project_found-form #email")
    email = email.toLowerCase()
    let catchdata: any = ""
    try {
      const data = { properties: { email: email, contact_id: id } }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data

      if (response.status === 200) {
        // (original left this branch empty)
      } else {
        // (original left this branch empty)
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function sha1Hash(email: string): Promise<string> {
__ea_trace("modules/entry/entry-found.ts","sha1Hash",[email],["email"]);
    const encoder = new TextEncoder()
    const data = encoder.encode(email)
    const hashBuffer = await crypto.subtle.digest("SHA-1", data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
    return hashHex
  }

  async function getContactIdByEmail(): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","getContactIdByEmail",[],[]);
    let eml = fieldVal("#project_found-form #email")
    eml = eml.toLowerCase()
    let catchdata: any = ""
    try {
      openLoader()
      const dt = { email: eml }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dt),
      })
      catchdata = dt
      const dtaa = await response.json()
      responseFromAPI = JSON.stringify(dtaa)
      if (response.status === 200) {
        const contactId = dtaa.userValue.id
        storage.set("contactId", contactId)
        await UpdateContactIdFromRecordId(contactId)
        const sha1email = await sha1Hash(eml)
        storage.set("sha1email", sha1email)
        ire("identify", {
          customerId: contactId,
          customerEmail: sha1email,
          customProfileId: storage.get("userId"),
        })
        await createDealRecord(contactId)
      } else {
        // (original left this branch empty)
      }
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function createDealRecord(contactId: any): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","createDealRecord",[contactId],["contactId"]);
    let emaill = fieldVal("#project_found-form #email")
    emaill = emaill.toLowerCase()
    const cntId = `New Enrollment - ${contactId}`
    let FarmStateAbbreviation: any = storage.get("FarmStateAbbreviation")
    if (FarmStateAbbreviation === "undefined") {
      FarmStateAbbreviation = ""
    }
    const zip_code = storage.get("zip_code")
    const county = storage.get("county")
    const utility_id = storage.get("utility_id")
    const FarmName = storage.get("FarmName")
    const FarmID = storage.get("FarmID")
    let impctValue: any = storage.get("isImpact")
    let clickId: any = storage.get("clickId")
    if (impctValue !== "Yes") {
      impctValue = "No"
    }
    if (clickId === "null" || clickId == null) {
      clickId = ""
    }
    let catchdata: any = ""
    try {
      const dataa = {
        properties: {
          deal_email_test: emaill,
          dealname: cntId,
          dealstage: "157471033",
          service_state_abbreviation: FarmStateAbbreviation,
          postal_code: zip_code,
          county: county,
          solar_farm_id: FarmID,
          solar_farm_name: FarmName,
          utility_id: utility_id,
          impact_referral: impctValue,
          impact_click_id: clickId,
        },
      }
      const response = await fetch(apiUrl("AddDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataa),
      })
      catchdata = dataa
      const dealResult = await response.json()
      responseFromAPI = JSON.stringify(dealResult)
      const dealId = dealResult.id
      if (response.status === 200) {
        storage.set("dealId", dealId)
        await dealAssociateWithContact(contactId, dealId)
        ire(
          "trackConversion",
          59966,
          {
            orderId: dealId,
            customProfileId: storage.get("userId"),
            customerId: contactId,
            customerEmail: storage.get("sha1email"),
          },
          { verifySiteDefinitionMatch: true },
        )
        clearDatabase()
      }

    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  async function dealAssociateWithContact(contactId: any, dealId: any): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","dealAssociateWithContact",[contactId,dealId],["contactId","dealId"]);
    let emailll = fieldVal("#project_found-form #email")
    emailll = emailll.toLowerCase()
    void emailll
    let catchdata: any = ""
    try {
      const dataaa = {
        inputs: [{ from: { id: contactId }, to: { id: dealId } }],
      }
      const response = await fetch(apiUrl("DealAssociateWithContact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataaa),
      })
      catchdata = dataaa
      if (response.status === 200) {
        await addedConfigIdWithSomeDataSignUp(dealOwner, dealId)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeLoader()
      console.error("Error:", error)
    }
  }

  // ─── HubDB session config ──────────────────────────────────────────────────────
  async function SaveSessionDetailsOnLocalStorageSignUp(): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","SaveSessionDetailsOnLocalStorageSignUp",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const hubDBEndpoint = hubdbRowsUrl("utilityAvailability", {
      utility_id__eq: String(exampleUtilityId),
    })
    const response = await fetch(hubDBEndpoint, { method: "GET" })
    const data = await response.json()
    const rows = data.objects
    if (rows.length >= 1) {
      storage.set("Config ID", rows[0].id)
      rows.forEach((element: any) => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[element],["element"]);
        if (exampleUtilityId === element.values["1"]) {
          storage.set("SessionDetails", JSON.stringify(element.values))
        } else {
          // (original left this branch empty)
        }
      })
    }
  }

  async function addedConfigIdWithSomeDataSignUp(rep: any, dealId: any): Promise<void> {
__ea_trace("modules/entry/entry-found.ts","addedConfigIdWithSomeDataSignUp",[rep,dealId],["rep","dealId"]);
    setTimeout(async () => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[],[]);
      if (rep === 0 || rep === "" || rep == null) {
        await SaveSessionDetailsOnLocalStorageSignUp()
        let email: any = fieldVal("#project_found-form #email")
        email = email.toLowerCase()
        const SessionLocalStorage = JSON.parse(storage.get("SessionDetails") ?? "null")
        const SessionConfigID = storage.get("Config ID")
        const utility_id = storage.get("utility_id")
        let utility_Name = ""
        const FarmName = storage.get("FarmName")
        const FarmID = storage.get("FarmID")
        const UserDevice = storage.get("User Device")
        const BrowserName = storage.get("Browser Name")
        const opratingSystem = storage.get("Operating System")
        let upd_attcode: any = storage.get("attcode")
        let upd_repid: any = storage.get("repid")
        let upd_referralUrl: any = storage.get("referralUrl")
        if (upd_attcode == null) {
          upd_attcode = ""
          upd_repid = ""
          upd_referralUrl = first_visited_url
        }
        const BillOption = SessionLocalStorage["40"]
        let utility_ext_id_1 = ""
        let utility_ext_id_1_system = ""
        let utility_ext_id_2 = ""
        let utility_ext_id_2_system = ""
        let utility_ext_id_3 = ""
        let utility_ext_id_3_system = ""
        if (SessionLocalStorage["6"]) {
          utility_Name = SessionLocalStorage["6"]
        }
        if (SessionLocalStorage["7"]) {
          utility_ext_id_1 = SessionLocalStorage["7"]
        }
        if (SessionLocalStorage["8"].name) {
          utility_ext_id_1_system = SessionLocalStorage["8"].name
        }
        if (SessionLocalStorage["9"]) {
          utility_ext_id_2 = SessionLocalStorage["9"]
        }
        if (SessionLocalStorage["10"]) {
          utility_ext_id_2_system = SessionLocalStorage["10"].name
        }
        if (SessionLocalStorage["11"]) {
          utility_ext_id_3 = SessionLocalStorage["11"]
        }
        if (SessionLocalStorage["12"]) {
          utility_ext_id_3_system = SessionLocalStorage["12"].name
        }
        if (upd_attcode === "undefined") {
          upd_attcode = ""
        }
        if (upd_repid === "undefined") {
          upd_repid = ""
        }
        if (upd_referralUrl === "undefined") {
          upd_referralUrl = first_visited_url
        }
        let catchdata: any = ""
        try {
          const data = {
            properties: {
              deal_id: dealId,
              solar_farm_id: FarmID,
              solar_farm_name: FarmName,
              utility_name: utility_Name,
              utility_id: utility_id,
              session_config_id: SessionConfigID,
              bill_option: BillOption,
              ext_id_1: utility_ext_id_1,
              ext_id_1_system: utility_ext_id_1_system,
              ext_id_2: utility_ext_id_2,
              ext_id_2_system: utility_ext_id_2_system,
              ext_id_3: utility_ext_id_3,
              ext_id_3_system: utility_ext_id_3_system,
              user_agent: BrowserName,
              user_device: UserDevice,
              user_agent___operating_system: opratingSystem,
              attribution_code: upd_attcode,
              attribution_rep: upd_repid,
              referral_url: upd_referralUrl,
            },
          }
          const response = await fetch(apiUrl("UpdateDealRecord"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          })
          catchdata = data
          closeLoader()
          storage.set("userEmail", email.toLowerCase())
          if (storage.get("skipUsage") !== null) {
            storage.remove("skipUsage")
          }
          if (response.status === 200) {
            window.location.href = "/my-info"
          }
          const result = await response.json()
          responseFromAPI = JSON.stringify(result)
        } catch (error) {
          console.error("Error:", error)
        }
        return true
      } else {
        await SaveSessionDetailsOnLocalStorageSignUp()
        let email: any = fieldVal("#project_found-form #email")
        email = email.toLowerCase()
        const SessionLocalStorage = JSON.parse(storage.get("SessionDetails") ?? "null")
        const SessionConfigID = storage.get("Config ID")
        const utility_id = storage.get("utility_id")
        let utility_Name = ""
        const FarmName = storage.get("FarmName")
        const FarmID = storage.get("FarmID")
        const UserDevice = storage.get("User Device")
        const BrowserName = storage.get("Browser Name")
        const opratingSystem = storage.get("Operating System")
        let upd_attcode: any = storage.get("attcode")
        let upd_repid: any = storage.get("repid")
        let upd_referralUrl: any = storage.get("referralUrl")
        if (upd_attcode == null) {
          upd_attcode = ""
          upd_repid = ""
          upd_referralUrl = first_visited_url
        } else {
          upd_referralUrl = first_visited_url
        }
        const BillOption = SessionLocalStorage["40"]
        let utility_ext_id_1 = ""
        let utility_ext_id_1_system = ""
        let utility_ext_id_2 = ""
        let utility_ext_id_2_system = ""
        let utility_ext_id_3 = ""
        let utility_ext_id_3_system = ""
        if (SessionLocalStorage["6"]) {
          utility_Name = SessionLocalStorage["6"]
        }
        if (SessionLocalStorage["7"]) {
          utility_ext_id_1 = SessionLocalStorage["7"]
        }
        if (SessionLocalStorage["8"].name) {
          utility_ext_id_1_system = SessionLocalStorage["8"].name
        }
        if (SessionLocalStorage["9"]) {
          utility_ext_id_2 = SessionLocalStorage["9"]
        }
        if (SessionLocalStorage["10"]) {
          utility_ext_id_2_system = SessionLocalStorage["10"].name
        }
        if (SessionLocalStorage["11"]) {
          utility_ext_id_3 = SessionLocalStorage["11"]
        }
        if (SessionLocalStorage["12"]) {
          utility_ext_id_3_system = SessionLocalStorage["12"].name
        }
        if (upd_attcode === "undefined") {
          upd_attcode = ""
        }
        if (upd_repid === "undefined") {
          upd_repid = ""
        }
        if (upd_referralUrl === "undefined") {
          upd_referralUrl = first_visited_url
        } else {
          upd_referralUrl = first_visited_url
        }
        let catchdata: any = ""
        try {
          const data = {
            properties: {
              deal_id: dealId,
              solar_farm_id: FarmID,
              solar_farm_name: FarmName,
              utility_name: utility_Name,
              utility_id: utility_id,
              session_config_id: SessionConfigID,
              bill_option: BillOption,
              ext_id_1: utility_ext_id_1,
              ext_id_1_system: utility_ext_id_1_system,
              ext_id_2: utility_ext_id_2,
              ext_id_2_system: utility_ext_id_2_system,
              ext_id_3: utility_ext_id_3,
              ext_id_3_system: utility_ext_id_3_system,
              user_agent: BrowserName,
              user_device: UserDevice,
              user_agent___operating_system: opratingSystem,
              hubspot_owner_id: rep,
              attribution_code: upd_attcode,
              attribution_rep: upd_repid,
              referral_url: upd_referralUrl,
            },
          }
          const response = await fetch(apiUrl("UpdateDealRecord"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          })
          catchdata = data
          closeLoader()
          storage.set("userEmail", email.toLowerCase())
          if (storage.get("skipUsage") !== null) {
            storage.remove("skipUsage")
          }
          if (response.status === 200) {
            window.location.href = "/my-info"
          }
          const result = await response.json()
          responseFromAPI = JSON.stringify(result)
        } catch (error) {
          console.error("Error:", error)
        }
        return true
      }
    }, 10000)
  }

  function openPopuppf(): void {
__ea_trace("modules/entry/entry-found.ts","openPopuppf",[],[]);
    q(".youtubevideo-video-pf").style.display = "block"
    q(".youtubevideo-thumb-pf").style.display = "none"
    const iframe = q(".youtubevideo-video-pf iframe") as HTMLIFrameElement
    const videoSrc = iframe.src
    if (!videoSrc.includes("autoplay=1")) {
      iframe.src = `${videoSrc + (videoSrc.includes("?") ? "&" : "?")}autoplay=1`
    }
  }
  void openPopuppf

  // ─── Wiring (was top-level in the inline script) ──────────────────────────────
  q("#logIn").addEventListener("click", () => {
__ea_trace("modules/entry/entry-found.ts","(anon)",[],[]);
    window.location.href = "/account"
  })

  qi("#email").addEventListener("input", validateEmail)
  qi("#password").addEventListener("input", validatePassword)
  q("#pff_submit").addEventListener("click", validateForm)

  const togglePasswordd = q("#togglePassword")
  const passwordd = qi("#password")
  togglePasswordd.addEventListener("click", function (this: HTMLElement) {
__ea_trace("modules/entry/entry-found.ts","(anon)",[this],["this"]);
    const type = passwordd.getAttribute("type") === "password" ? "text" : "password"
    passwordd.setAttribute("type", type)
    this.classList.toggle("fa-eye-slash")
  })
  $(root)
    .find("form i#togglePassword")
    .on("click", function (this: HTMLElement) {
__ea_trace("modules/entry/entry-found.ts","(anon)",[this],["this"]);
      $(this).toggleClass("colored")
    })
}
